import React from 'react';
import ProtectedRoute from './routes/ProtectedRoute';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ApiAuthProvider } from './contexts/ApiAuthContext';
import { ActivityLogProvider } from './contexts/ActivityLogContext';
import Layout from './components/layout/Layout';

import Dashboard from './features/dashboard/pages/DashboardPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import Tasks from './features/projects/pages/TasksPage';
import Settings from './features/settings/pages/SettingsPage';
import { usersPublicRoutes, usersProtectedRoutes } from './features/users/routes';
import { projectsRoutes } from './features/projects/routes';
import { resourcesRoutes } from './features/resources/routes';
import { competitionsRoutes } from './features/competitions/routes';
import { clientsRoutes } from './features/clients/routes';
import { prRoutes } from './features/pr/routes';
import { graphicsRoutes } from './features/graphics/routes';
import { reportsRoutes } from './features/reports/routes';
import { calendarRoutes } from './features/calendar/routes';
import { marketingRoutes } from './features/marketing/routes';
import { eventsRoutes } from './features/events/routes';

function App() {
  const groupedProtectedRoutes = [
    ...projectsRoutes,
    ...usersProtectedRoutes,
    ...resourcesRoutes,
    ...clientsRoutes,
    ...competitionsRoutes,
    ...prRoutes,
    ...graphicsRoutes,
    ...reportsRoutes,
    ...calendarRoutes,
    ...marketingRoutes,
    ...eventsRoutes,
  ];

  return (
    <ApiAuthProvider>
      <ActivityLogProvider>
        <Router>
          <Toaster
            position="top-right"
            gutter={10}
            toastOptions={{
              duration: 4000,
              style: {
                background: '#ffffff',
                color: '#1e293b',
                borderRadius: '14px',
                padding: '12px 16px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
                fontSize: '13.5px',
                fontWeight: '500',
                maxWidth: '360px',
                lineHeight: '1.5',
              },
              success: {
                iconTheme: { primary: '#15803d', secondary: '#dcfce7' },
                style: {
                  border: '1px solid #dcfce7',
                },
              },
              error: {
                iconTheme: { primary: '#b91c1c', secondary: '#fee2e2' },
                style: {
                  border: '1px solid #fee2e2',
                },
              },
              loading: {
                iconTheme: { primary: '#37559e', secondary: '#bfdbfe' },
                style: {
                  border: '1px solid #bfdbfe',
                },
              },
            }}
          />
          <Routes>
              {/* Public routes */}
              {usersPublicRoutes.map(({ path, component: Component }) => (
                <Route key={path} path={path} element={<Component />} />
              ))}

              {/* Unauthorized — standalone, outside Layout; auto-logs out on arrival */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              
              
              {/* Protected routes */}
              <Route path="/" element={<Layout />}>
                {/* Dashboard - Requires dashboard.view permission */}
                <Route index element={
                  <ProtectedRoute requiredPermission="dashboard.view">
                    <Dashboard />
                  </ProtectedRoute>
                } />
                
                {/* Tasks - Requires tasks.view permission */}
                <Route path="tasks" element={
                  <ProtectedRoute requiredPermission="tasks.view">
                    <Tasks />
                  </ProtectedRoute>
                } />

                {groupedProtectedRoutes.map(({ path, component: Component, requiredPermission }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute requiredPermission={requiredPermission}>
                        <Component />
                      </ProtectedRoute>
                    }
                  />
                ))}
                
                {/* Settings - authenticated users only, no specific permission */}
                <Route path="settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                
              </Route>
            </Routes>
          </Router>
        </ActivityLogProvider>
      </ApiAuthProvider>
  );
}

export default App;